import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Backdrop, Fade, Box, CircularProgress, Typography } from "@mui/material";
import { keyframes } from "@mui/system";

const pulse = keyframes`
  0% { transform: scale(1); opacity: .9; }
  50% { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(1); opacity: .9; }
`;

/**
 * 🔹 Imperative LoadingSplash
 *  Methods available on ref:
 *   - show(message?: string)
 *   - hide()
 *   - setMessage(message: string)
 */
const LoadingSplash = forwardRef(function LoadingSplash(
    {
        defaultMessage = "در حال بارگذاری...",
        logoSrc = "https://hamekara.com/hamekara-logo.png",
        spinnerColor = "#9a1111",
        background = "rgba(255,255,255,0.95)",
        minDuration = 600, // prevent flicker
    },
    ref
) {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState(defaultMessage);
    const openedAtRef = useRef(0);

    useImperativeHandle(ref, () => ({
        show: (msg) => {
            if (msg) setMessage(msg);
            openedAtRef.current = Date.now();
            setOpen(true);
        },
        hide: () => {
            const elapsed = Date.now() - openedAtRef.current;
            const delay = Math.max(0, minDuration - elapsed);
            setTimeout(() => setOpen(false), delay);
        },
        setMessage: (msg) => setMessage(msg || defaultMessage),
    }));

    return (
        <Backdrop
            open={open}
            sx={{
                zIndex: (t) => t.zIndex.modal + 9999,
                bgcolor: background,
                backdropFilter: "blur(6px)",
            }}
        >
            <Fade in={open} timeout={{ enter: 300, exit: 250 }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                    <Box
                        component="img"
                        src={logoSrc}
                        alt="Hamekara Logo"
                        sx={{
                            width: 160,
                            height: "auto",
                            animation: `${pulse} 2s ease-in-out infinite`,
                            userSelect: "none",
                        }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#222", direction: "rtl" }}>
                        {message}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CircularProgress size={22} thickness={5} sx={{ color: spinnerColor }} />
                        <Typography variant="body2" sx={{ color: "#555" }}>
                            لطفاً صبر کنید…
                        </Typography>
                    </Box>
                </Box>
            </Fade>
        </Backdrop>
    );
});

export default LoadingSplash;
